import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';

// SVG charts for the v2 Branches page. Every colour is a CSS class whose
// stroke / fill is a --v2-* token (BranchesV2.css), so the charts switch with
// the light / dark theme without a re-render. Tones: ink, sage, slate,
// amber, red, teal.

/** Read-out position: centred on the point, kept inside the chart near its edges. */
const tipStyle = (f) => {
    if (f > 0.8) return { left: 'auto', right: `${(1 - f) * 100}%`, transform: 'translate(12px, 0)' };
    if (f < 0.2) return { left: `${f * 100}%`, transform: 'translate(-12px, 0)' };
    return { left: `${f * 100}%` };
};

const niceStep = (span, target = 5) => {
    const raw = span / target || 1;
    const mag = 10 ** Math.floor(Math.log10(raw));
    const n = raw / mag;
    return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * mag;
};

/** Y scale: round ticks covering every value (and 0 when asked). */
export const yScale = (values, { fromZero = false } = {}) => {
    const vals = values.filter((v) => v != null && Number.isFinite(v));
    if (!vals.length) return { lo: 0, hi: 1, ticks: [0, 1] };
    let lo = Math.min(...vals);
    let hi = Math.max(...vals);
    if (fromZero) lo = Math.min(0, lo);
    if (hi === lo) { hi += Math.abs(hi) * 0.1 || 1; lo -= fromZero ? 0 : Math.abs(lo) * 0.1 || 1; }
    const step = niceStep(hi - lo);
    const y0 = Math.floor(lo / step) * step;
    const y1 = Math.ceil(hi / step) * step;
    const ticks = [];
    for (let v = y0; v <= y1 + step / 1e6; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
    return { lo: y0, hi: y1, ticks };
};

/**
 * End-label y positions that do not overlap: labels sorted top to bottom,
 * each at least `gap` below the one above (QA BR-6). Keys → y.
 */
export const endLabelPositions = (items, gap) => {
    const out = {};
    let prev = -Infinity;
    [...items].sort((a, b) => a.y - b.y).forEach((it) => {
        const y = Math.max(it.y, prev + gap);
        out[it.key] = y;
        prev = y;
    });
    return out;
};

/**
 * Line chart with a crosshair read-out. Hover reads the nearest point; the
 * chart is also focusable and ←/→ (Home/End) step through the points, the
 * read-out announced politely; the read point gets a marker on every line and
 * a faint vertical guide. A visually hidden table carries every value.
 * `formatTip(value, index, series)` formats a point in the read-out and the
 * table (the index and series let it add context, e.g. a count).
 */
export function LineChart({
    label, labels, series, formatY, formatTip, height = 240, width = 640, fromZero = false, tipTitle, endLabels = true,
    subLabels = null, pointLabels = null,
}) {
    const W = width;
    const H = height;
    const L = 56;
    const R = endLabels ? 46 : 14;
    const T = 12;
    const B = subLabels ? 38 : 26;
    const id = useId().replace(/:/g, '');
    const [index, setIndex] = useState(null);
    const scale = yScale(series.flatMap((s) => s.values), { fromZero });
    const n = labels.length;
    const X = (i) => L + (n > 1 ? i / (n - 1) : 0.5) * (W - L - R);
    const Y = (v) => T + ((scale.hi - v) / (scale.hi - scale.lo || 1)) * (H - T - B);
    const lastWithData = () => {
        for (let i = n - 1; i >= 0; i -= 1) if (series.some((s) => s.values[i] != null)) return i;
        return n - 1;
    };

    const onKeyDown = (e) => {
        const moves = {
            ArrowRight: (i) => Math.min(n - 1, i + 1),
            ArrowLeft: (i) => Math.max(0, i - 1),
            Home: () => 0,
            End: () => n - 1,
        };
        const move = moves[e.key];
        if (!move) return;
        e.preventDefault();
        setIndex((i) => move(i == null ? lastWithData() : i));
    };
    const onMouseMove = (e) => {
        const box = e.currentTarget.getBoundingClientRect();
        if (!box.width) return;
        const px = ((e.clientX - box.left) / box.width) * W;
        const i = Math.round(((px - L) / (W - L - R)) * (n - 1));
        if (i >= 0 && i < n) setIndex(i);
    };

    // The read-out sits at the top unless a read point is in the upper half;
    // then it moves to the bottom, so it never covers the points it describes.
    const tipVertical = (i) => {
        const ys = series.map((s) => s.values[i]).filter((v) => v != null).map(Y);
        if (!ys.length || Math.min(...ys) > H * 0.5) return {};
        return { top: 'auto', bottom: `${((B + 6) / H) * 100}%` };
    };

    const ends = series.map((s) => {
        let last = -1;
        s.values.forEach((v, i) => { if (v != null) last = i; });
        return { key: s.name, last, y: last >= 0 ? Y(s.values[last]) + 4 : 0 };
    });
    const labelY = endLabelPositions(ends.filter((e) => e.last >= 0), 12);

    const path = (values) => {
        let d = '';
        let started = false;
        values.forEach((v, i) => {
            if (v == null) { started = false; return; }
            d += `${started ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`;
            started = true;
        });
        return d;
    };

    return (
        <div className="br2-chart">
            <div
                className="br2-chart-box"
                tabIndex={0}
                role="group"
                aria-label={`${label}. Use the arrow keys to read each point.`}
                aria-describedby={index != null ? `${id}-tip` : undefined}
                onKeyDown={onKeyDown}
                onFocus={() => setIndex((i) => (i == null ? lastWithData() : i))}
                onBlur={() => setIndex(null)}
                onMouseMove={onMouseMove}
                onMouseLeave={() => setIndex(null)}
                data-testid="line-chart"
            >
                <svg viewBox={`0 0 ${W} ${H}`} aria-hidden="true" focusable="false">
                    {scale.ticks.map((v) => (
                        <g key={v}>
                            <line className="br2-gl" x1={L} x2={W - R} y1={Y(v)} y2={Y(v)} />
                            <text className="br2-ax" x={L - 6} y={Y(v) + 3.5} textAnchor="end">{formatY(v)}</text>
                        </g>
                    ))}
                    {labels.map((l, i) => (
                        <text key={`${l}-${i}`} className="br2-ax br2-ax-x" x={X(i)} y={H - (subLabels ? 20 : 8)} textAnchor="middle">{l}</text>
                    ))}
                    {subLabels && subLabels.map((l, i) => (l ? (
                        <text key={`y-${l}-${i}`} className="br2-ax br2-ax-yr" x={X(i)} y={H - 6} textAnchor="middle">{l}</text>
                    ) : null))}
                    {index != null && <line className="br2-cross" x1={X(index)} x2={X(index)} y1={T} y2={H - B} />}
                    {series.map((s, si) => {
                        const { last } = ends[si];
                        return (
                            <g key={s.name} data-series={s.name}>
                                <path className={`br2-ln br2-ln--${s.tone}${s.dashed ? ' is-dashed' : ''}${s.emphasis ? ' is-strong' : ''}`} d={path(s.values)} />
                                {last >= 0 && (
                                    <>
                                        <circle className={`br2-ln-end br2-ln--${s.tone}`} cx={X(last)} cy={Y(s.values[last])} r={s.emphasis ? 4.5 : 3.5} />
                                        {endLabels && <text className="br2-dl" x={X(last) + 7} y={labelY[s.name]}>{s.name}</text>}
                                    </>
                                )}
                            </g>
                        );
                    })}
                    {index != null && series.map((s) => (s.values[index] == null ? null : (
                        <circle
                            key={`pt-${s.name}`}
                            className={`br2-ln-pt br2-ln--${s.tone}`}
                            cx={X(index)}
                            cy={Y(s.values[index])}
                            r={s.emphasis ? 4.5 : 3.5}
                        />
                    )))}
                </svg>
                <div
                    id={`${id}-tip`}
                    className="br2-chart-tip"
                    role="status"
                    aria-live="polite"
                    hidden={index == null}
                    style={index != null ? { ...tipStyle(X(index) / W), ...tipVertical(index) } : undefined}
                >
                    {index != null && (
                        <>
                            <b>{tipTitle ? tipTitle(index) : labels[index]}</b>
                            {series.map((s) => (
                                <span key={s.name}>{s.name}: {s.values[index] == null ? '—' : formatTip(s.values[index], index, s)}</span>
                            ))}
                        </>
                    )}
                </div>
            </div>
            <table className="v2-sr">
                <caption>{label}</caption>
                <thead>
                    <tr><th scope="col">Point</th>{series.map((s) => <th key={s.name} scope="col">{s.name}</th>)}</tr>
                </thead>
                <tbody>
                    {labels.map((l, i) => (
                        <tr key={`${l}-${i}`}>
                            <th scope="row">{pointLabels ? pointLabels[i] : l}</th>
                            {series.map((s) => <td key={s.name}>{s.values[i] == null ? '—' : formatTip(s.values[i], i, s)}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

LineChart.propTypes = {
    label: PropTypes.string.isRequired,
    labels: PropTypes.arrayOf(PropTypes.string).isRequired,
    series: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        values: PropTypes.array.isRequired,
        tone: PropTypes.string.isRequired,
        dashed: PropTypes.bool,
        emphasis: PropTypes.bool,
    })).isRequired,
    formatY: PropTypes.func.isRequired,
    formatTip: PropTypes.func.isRequired,
    height: PropTypes.number,
    width: PropTypes.number,
    fromZero: PropTypes.bool,
    tipTitle: PropTypes.func,
    /** Name each line at its end (off: a legend names them instead). */
    endLabels: PropTypes.bool,
    /** A second axis line under the labels (e.g. the year on January); null entries are blank. */
    subLabels: PropTypes.arrayOf(PropTypes.string),
    /** Full point names for the screen-reader table (default: the labels). */
    pointLabels: PropTypes.arrayOf(PropTypes.string),
};

const signedPct = (v) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(1)}%`;

/** Rolling price YoY bars (3M / 6M / 12M): red below zero, sage above. */
export function YoyBars({ values, labels }) {
    const W = 300;
    const H = 200;
    const L = 40;
    const T = 18;
    const B = 28;
    const scale = yScale([...values, 0], { fromZero: true });
    const Y = (v) => T + ((scale.hi - v) / (scale.hi - scale.lo || 1)) * (H - T - B);
    const desc = labels.map((l, i) => `${l} ${values[i] == null ? 'no value' : signedPct(values[i])}`).join(', ');
    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="br2-yoy" role="img" aria-label={`Rolling average price year on year: ${desc}`} data-testid="yoy-bars">
            {scale.ticks.map((v) => (
                <g key={v}>
                    <line className={v === 0 ? 'br2-gl br2-gl--zero' : 'br2-gl'} x1={L} x2={W} y1={Y(v)} y2={Y(v)} />
                    <text className="br2-ax" x={L - 5} y={Y(v) + 3.5} textAnchor="end">{`${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v)}%`}</text>
                </g>
            ))}
            {labels.map((l, i) => {
                const v = values[i];
                const x = L + 22 + i * ((W - L - 44) / labels.length);
                const bw = 40;
                const y = v == null ? Y(0) : Math.min(Y(0), Y(v));
                const h = v == null ? 0 : Math.max(1, Math.abs(Y(v) - Y(0)));
                return (
                    <g key={l}>
                        {v != null && <rect className={v < 0 ? 'br2-bar--neg' : 'br2-bar--pos'} x={x} y={y} width={bw} height={h} rx="4" />}
                        <text className="br2-dl" x={x + bw / 2} y={v == null ? Y(0) - 5 : (v < 0 ? Y(v) + 14 : Y(v) - 5)} textAnchor="middle">
                            {v == null ? '—' : signedPct(v)}
                        </text>
                        <text className="br2-ax" x={x + bw / 2} y={H - 8} textAnchor="middle">{l}</text>
                    </g>
                );
            })}
        </svg>
    );
}

YoyBars.propTypes = { values: PropTypes.array.isRequired, labels: PropTypes.arrayOf(PropTypes.string).isRequired };

/** 12-month trend sparkline (decorative). */
export function Sparkline({ data, width = 96, height = 22 }) {
    if (!data || data.length < 2) return null;
    const lo = Math.min(...data);
    const hi = Math.max(...data);
    const X = (i) => 2 + (i / (data.length - 1)) * (width - 6);
    const Y = (v) => 3 + ((hi - v) / ((hi - lo) || 1)) * (height - 6);
    const d = data.map((v, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`).join('');
    return (
        <svg className="br2-spark" width={width} height={height} aria-hidden="true" focusable="false">
            <path d={d} />
            <circle cx={X(data.length - 1)} cy={Y(data[data.length - 1])} r="2.5" />
        </svg>
    );
}

Sparkline.propTypes = { data: PropTypes.arrayOf(PropTypes.number), width: PropTypes.number, height: PropTypes.number };
